import { useEffect, useState } from "react";

import dayjs from "dayjs";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAvailability } from "@/hooks/use-availability";
import { useCalendarDisabled } from "@/hooks/use-calendar-disabled";
import { useDefineHold, useExtendHold, useQuoteActions } from "@/hooks/use-cotizaciones";
import { useActiveLocationTimezone } from "@/hooks/use-locations";
import { notify } from "@/store/store";
import type { ApiQuoteRequest } from "@/types/api";
import { formatTime } from "@/utils/date";

interface Props {
  quote: ApiQuoteRequest | null;
  onClose: () => void;
}

export function CotizacionDetailDialog({ quote, onClose }: Props) {
  const defineHold = useDefineHold();
  const extendHold = useExtendHold();
  const { reservar, descartar } = useQuoteActions(onClose);

  const isDateDisabled = useCalendarDisabled();
  const locationTz = useActiveLocationTimezone();

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedStartsAt, setSelectedStartsAt] = useState<string>("");
  const [pax, setPax] = useState("");

  const hasActiveHold = quote?.hold?.status === "active";
  const isTerminal = quote?.status === "converted" || quote?.status === "dismissed";

  // Seed the editable fields from the active hold (its pinned slot/pax) when one
  // exists, else from the guest's tentative snapshot. Seeding the hold's own slot
  // as ``selectedStartsAt`` lets the operator change only pax without re-picking.
  useEffect(() => {
    if (!quote) return;
    const hold = quote.hold?.status === "active" ? quote.hold : null;
    if (hold) {
      const local = locationTz ? dayjs(hold.starts_at).tz(locationTz) : dayjs(hold.starts_at);
      setSelectedDate(dayjs(local.format("YYYY-MM-DD")).toDate());
      setSelectedStartsAt(hold.starts_at);
      setPax(String(hold.pax));
      return;
    }
    setSelectedDate(quote.tentative_date ? dayjs(quote.tentative_date).toDate() : undefined);
    setSelectedStartsAt("");
    setPax(String(quote.pax));
    // Re-seed only when a different quote opens; isDateDisabled/locationTz are stable enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.id]);

  const targetDate = selectedDate ? dayjs(selectedDate).format("YYYY-MM-DD") : "";
  const { data: availabilityData, isLoading: availabilityLoading } = useAvailability({
    targetDate,
    pax: quote?.pax ?? 1,
    enabled: !!selectedDate,
  });

  const slotOptions = (availabilityData?.slots ?? []).map((s) => ({
    value: s.starts_at,
    label: formatTime(locationTz ? dayjs(s.starts_at).tz(locationTz) : dayjs(s.starts_at)),
  }));
  // Keep the currently-selected slot (e.g. the hold's own time) available as an
  // option even when availability didn't return it, so the select can show it.
  if (selectedStartsAt && !slotOptions.some((o) => o.value === selectedStartsAt)) {
    slotOptions.unshift({
      value: selectedStartsAt,
      label: formatTime(
        locationTz ? dayjs(selectedStartsAt).tz(locationTz) : dayjs(selectedStartsAt),
      ),
    });
  }

  function handleDefine() {
    if (!quote || !selectedStartsAt) return;
    defineHold.mutate(
      {
        id: quote.id,
        body: {
          starts_at: selectedStartsAt,
          pax: pax ? Number(pax) : quote.pax,
        },
      },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <Dialog
      open={quote !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{quote?.guest_name ?? ""}</DialogTitle>
        </DialogHeader>

        {quote && (
          <div className="space-y-3">
            <div className="text-sm">
              <span className="text-muted-foreground">Email: </span>
              {quote.guest_email}
            </div>

            <div className="text-sm">
              <span className="text-muted-foreground">Teléfono: </span>
              {quote.guest_phone}
            </div>

            <div className="text-sm">
              <span className="text-muted-foreground">Personas: </span>
              {quote.pax}
            </div>

            {quote.notes ? (
              <div className="text-sm">
                <span className="text-muted-foreground">Notas adicionales: </span>
                {quote.notes}
              </div>
            ) : null}

            {hasActiveHold && (
              <div className="space-y-1 rounded-lg border p-3">
                <p className="text-sm font-medium">Hold activo</p>
                <p className="text-sm text-muted-foreground">
                  Vence {new Date(quote.hold!.expires_at).toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground" data-testid="hold-tables-dialog">
                  {quote.hold!.table_names.length > 0
                    ? `Mesas del hold: ${quote.hold!.table_names.join(", ")}`
                    : "Sin mesa asignada"}
                </p>
              </div>
            )}

            {!isTerminal && (
              <div className="space-y-3 rounded-lg border p-3">
                <p className="text-sm font-medium">
                  {hasActiveHold ? "Editar hold" : "Definir hold"}
                </p>
                <div className="space-y-1">
                  <Label>Fecha</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="w-full justify-start">
                        {selectedDate ? dayjs(selectedDate).format("DD/MM/YYYY") : "Elegir fecha"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(d) => {
                          setSelectedDate(d);
                          setSelectedStartsAt("");
                        }}
                        disabled={isDateDisabled}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="hold-time">Hora</Label>
                  <select
                    id="hold-time"
                    aria-label="Hora"
                    value={selectedStartsAt}
                    onChange={(e) => setSelectedStartsAt(e.target.value)}
                    disabled={!selectedDate || availabilityLoading}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-50"
                  >
                    {!selectedDate ? (
                      <option value="" disabled>
                        Hora
                      </option>
                    ) : availabilityLoading ? (
                      <option value="" disabled>
                        Cargando…
                      </option>
                    ) : slotOptions.length === 0 ? (
                      <option value="" disabled>
                        Sin horarios disponibles
                      </option>
                    ) : (
                      <>
                        <option value="" disabled>
                          Hora
                        </option>
                        {slotOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="hold-pax">Personas</Label>
                  <Input
                    id="hold-pax"
                    type="number"
                    min={1}
                    placeholder={String(quote.pax)}
                    value={pax}
                    onChange={(e) => setPax(e.target.value)}
                  />
                </div>
                <Button
                  size="sm"
                  onClick={handleDefine}
                  disabled={!selectedStartsAt || defineHold.isPending}
                >
                  {hasActiveHold ? "Guardar cambios" : "Definir hold"}
                </Button>
              </div>
            )}

            {!isTerminal && (
              <div className="flex gap-2 pt-1">
                {hasActiveHold && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      extendHold.mutate(
                        { id: quote.id, body: { hours: 30 } },
                        { onSuccess: () => notify("success", "Hold extendido", "") },
                      )
                    }
                  >
                    Extender (+30 h)
                  </Button>
                )}
                <Button size="sm" onClick={() => reservar(quote)}>
                  Reservar
                </Button>
                <Button size="sm" variant="destructive" onClick={() => descartar(quote)}>
                  Descartar
                </Button>
              </div>
            )}
          </div>
        )}

        <DialogFooter showCloseButton />
      </DialogContent>
    </Dialog>
  );
}
